import { Field } from '@today/ui-components'
import { Switch } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { SaveRow, SettingsSection } from './settings-section'

/** Copy of {@link OfferContactSettingsView}; each omitted key falls back to English. */
export interface OfferContactSettingsLabels {
	title: string
	description: string
	toggle: string
	phone: string
	phoneHelp: string
	email: string
	emailHelp: string
	preview: string
	photoAlt: string
	save: string
	saved: string
}

const DEFAULT_LABELS: OfferContactSettingsLabels = {
	title: 'Contact on offers',
	description: 'When on, the offers you send show your own contact card instead of the branch card.',
	toggle: 'Show my contact on offers',
	phone: 'Direct phone',
	phoneHelp: 'International format, e.g. +34981000111. Empty uses your identity-provider phone.',
	email: 'Contact email',
	emailHelp: 'Empty uses your account email.',
	preview: 'Customers will see',
	photoAlt: 'Your profile photo',
	save: 'Save contact',
	saved: 'Contact saved.',
}

/** The card a landing would show, as the API derives it. */
export interface OfferContactCardPreview {
	name: string
	picture?: string
	phone?: string
	email?: string
}

/**
 * Props for {@link OfferContactSettingsView}.
 *
 * @property phone / email - The draft overrides ('' = no override).
 * @property phoneError / emailError - Refusals of the draft, shown on each field.
 * @property card - The card the saved settings produce; previewed while `enabled`.
 */
export interface OfferContactSettingsViewProps {
	enabled: boolean
	onEnabledChange: (enabled: boolean) => void
	phone: string
	onPhoneChange: (value: string) => void
	phoneError?: string | null
	email: string
	onEmailChange: (value: string) => void
	emailError?: string | null
	card: OfferContactCardPreview
	onSave: () => void
	isSaving?: boolean
	error?: string | null
	saved?: boolean
	labels?: Partial<OfferContactSettingsLabels>
}

/** The card preview of {@link OfferContactSettingsView}. */
function CardPreview({
	card,
	text,
}: {
	card: OfferContactCardPreview
	text: OfferContactSettingsLabels
}): ReactElement {
	return (
		<figure className="flex items-center gap-4 rounded-ds-md border border-ds-border bg-ds-surface-muted p-4">
			{card.picture ? (
				<img
					src={card.picture}
					alt={text.photoAlt}
					width={64}
					height={64}
					referrerPolicy="no-referrer"
					className="rounded-ds-full"
				/>
			) : null}
			<figcaption className="flex flex-col gap-1 text-ds-sm text-ds-text">
				<span className="text-ds-text">{text.preview}</span>
				<span className="font-semibold">{card.name}</span>
				{card.phone ? <span>{card.phone}</span> : null}
				{card.email ? <span>{card.email}</span> : null}
			</figcaption>
		</figure>
	)
}

/** The Settings "Contact on offers" section: the opt-in toggle, overrides and card preview (organism, TO-197). */
export function OfferContactSettingsView(props: OfferContactSettingsViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<SettingsSection title={text.title} description={text.description} error={props.error}>
			<Switch isSelected={props.enabled} onChange={props.onEnabledChange}>
				{text.toggle}
			</Switch>
			<Field
				label={text.phone}
				type="tel"
				value={props.phone}
				onChange={(event) => props.onPhoneChange(event.target.value)}
				aria-invalid={props.phoneError ? true : undefined}
				description={props.phoneError ?? text.phoneHelp}
			/>
			<Field
				label={text.email}
				type="email"
				value={props.email}
				onChange={(event) => props.onEmailChange(event.target.value)}
				aria-invalid={props.emailError ? true : undefined}
				description={props.emailError ?? text.emailHelp}
			/>
			{props.enabled ? <CardPreview card={props.card} text={text} /> : null}
			<SaveRow
				label={text.save}
				savedLabel={text.saved}
				onSave={props.onSave}
				isSaving={props.isSaving}
				saved={props.saved}
			/>
		</SettingsSection>
	)
}
